/**
 * @fileoverview Local-first import template service for system-web.
 * @module system-web/import-templates
 *
 * Responsibilities:
 * - Serve import template reads from Dexie `import_templates`
 * - Write template mutations to Dexie first and wake the system sync loop
 * - Project full template documents into grid/list row shapes for library views
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for Dexie access and wake notifications
 * - `./systemAuditLogService` for local audit trail writes
 *
 * Architecture:
 * - Reads are local-first from Dexie only
 * - UI components consume this service instead of calling API clients directly
 * - Normal create, update, clone, and delete mutations are Dexie-first and sync-driven
 *
 * Notes:
 * - Department-scoped templates are read-only in system-web
 * - Draft and published state must remain consistent between editor and grid/run flows
 */

import { generateObjectId, getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import type { ImportTemplate, ImportTemplateListItem } from '@firesquirrel/local-db';
// Deep import (not the package barrel) on purpose: the `@firesquirrel/import-editor`
// barrel re-exports the whole React component tree (UnifiedImportWizard, grids,
// lucide-react icons). This service is a pure Dexie data-access layer and must not
// pull the UI layer into its module graph.
import { toListItem, sortGrouped } from '@firesquirrel/import-editor/utils/templateProjection';
import { writeAuditLog } from './systemAuditLogService';

function normalizeAllowedTenantIds(template: Pick<ImportTemplate, 'visibility' | 'allowedTenantIds'>): string[] {
  return template.visibility === 'specific-tenants' ? template.allowedTenantIds ?? [] : [];
}

// ---------------------------------------------------------------------------
// List templates — local-first read from Dexie only.
// Optional tenantId lets callers (e.g. managed import wizard) filter the
// list to templates relevant to a specific department while keeping the
// underlying cache shared. `toListItem` is called without `defaultScope`;
// the shared helper defaults to `'system'` when a doc omits `scope` (G-043),
// so the `ImportTemplateListItem.scope` (REQUIRED) contract is always
// satisfied. System-web is the system admin portal — a system-DB
// `importTemplates` document missing `scope` is most likely legacy system
// data, so `'system'` is the safe default.
// ---------------------------------------------------------------------------
export async function listImportTemplates(tenantId?: string): Promise<ImportTemplateListItem[]> {
  const db = await getSystemDb();

  const localDocs = await db.import_templates.toArray();
  const local = (localDocs as any[])
    .filter(t => !t._deleted)
    .filter(t => {
      if (!tenantId) return true;
      const tid = (t as any).tenantId as string | undefined;
      // System-scope templates (no tenantId) are always visible; department
      // templates are scoped to their owning tenantId.
      return !tid || tid === tenantId;
    })
    .map(d => toListItem(d));

  return sortGrouped(local);
}

export async function getCachedImportTemplate(id: string): Promise<ImportTemplate | null> {
  const db = await getSystemDb();
  const localDoc = await db.import_templates.get(id);
  if (!localDoc || (localDoc as any)._deleted) return null;
  return localDoc as ImportTemplate;
}

// ---------------------------------------------------------------------------
// Get single template — local-first read from Dexie only.
// ---------------------------------------------------------------------------
export async function getImportTemplate(id: string): Promise<ImportTemplate | null> {
  return getCachedImportTemplate(id);
}

// ---------------------------------------------------------------------------
// Create a new system template — Dexie first, then sync push.
// ---------------------------------------------------------------------------
export async function createImportTemplate(
  input: Omit<ImportTemplate, '_id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'scope'>,
): Promise<ImportTemplate> {
  const db = await getSystemDb();
  const now = new Date().toISOString();
  const isDraft = input.isDraft === true || input.status === 'draft';
  const data: ImportTemplate = {
    ...input,
    _id: generateObjectId(),
    scope: 'system',
    origin: input.origin ?? 'system-web',
    allowedTenantIds: normalizeAllowedTenantIds(input),
    fieldMappings: input.fieldMappings ?? [],
    status: input.status ?? (isDraft ? 'draft' : 'published'),
    isDraft,
    totalRuns: input.totalRuns ?? 0,
    createdAt: now,
    createdBy: 'system-web',
    updatedAt: now,
    updatedBy: 'system-web',
  };
  await db.import_templates.put(data as any);
  wakeLocalWrite('import_templates');
  writeAuditLog({
    collection: 'importTemplates',
    documentId: data._id,
    action: 'create',
    summary: `Created import template "${data.name}"`,
  }).catch((err) => {
    console.warn('[systemImportTemplateService] Failed to write audit log for template creation:', err);
  });
  return data;
}

// ---------------------------------------------------------------------------
// Update a system template — Dexie first, then sync push.
// ---------------------------------------------------------------------------
export async function updateImportTemplate(
  id: string,
  patch: Partial<ImportTemplate>,
): Promise<ImportTemplate> {
  const db = await getSystemDb();
  const existing = await db.import_templates.get(id) as ImportTemplate | undefined;
  if (!existing || (existing as any)._deleted) {
    throw new Error('Import template not found in local database. Please allow sync to finish and try again.');
  }
  const now = new Date().toISOString();
  const nextStatus = patch.status ?? existing.status;
  const nextIsDraft = patch.isDraft ?? existing.isDraft ?? nextStatus === 'draft';
  const keepDraftArtifacts = nextStatus === 'draft' || nextIsDraft === true;
  const data: ImportTemplate = {
    ...existing,
    ...patch,
    allowedTenantIds: normalizeAllowedTenantIds({
      visibility: patch.visibility ?? existing.visibility,
      allowedTenantIds: patch.allowedTenantIds ?? existing.allowedTenantIds,
    }),
    samplePreview: keepDraftArtifacts ? patch.samplePreview ?? existing.samplePreview : undefined,
    draftResume: keepDraftArtifacts ? patch.draftResume ?? existing.draftResume : undefined,
    isDraft: nextIsDraft,
    // G-014: bump `version` on every update so consumers can detect stale
    // reads. Existing rows that predate the version field start at 0 → 1.
    version: ((existing as any).version ?? 0) + 1,
    updatedAt: now,
    updatedBy: 'system-web',
  };
  await db.import_templates.put(data as any);
  wakeLocalWrite('import_templates');
  writeAuditLog({
    collection: 'importTemplates',
    documentId: id,
    action: 'update',
    summary: `Updated import template "${data.name}"`,
  }).catch((err) => {
    console.warn('[systemImportTemplateService] Failed to write audit log for template update:', err);
  });
  return data;
}

// ---------------------------------------------------------------------------
// Delete a system template — Dexie tombstone first, then sync push.
// ---------------------------------------------------------------------------
export async function deleteImportTemplate(id: string): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.import_templates.get(id);
  const name = existing ? (existing as any).name ?? id : id;
  const now = new Date().toISOString();
  await db.import_templates.put({
    ...(existing as any),
    _id: id,
    _deleted: true,
    deletedAt: now,
    deletedBy: 'system-web',
    updatedAt: now,
    updatedBy: 'system-web',
  });
  wakeLocalWrite('import_templates');
  writeAuditLog({
    collection: 'importTemplates',
    documentId: id,
    action: 'delete',
    summary: `Deleted import template "${name}"`,
  }).catch((err) => {
    console.warn('[systemImportTemplateService] Failed to write audit log for template delete:', err);
  });
}

// ---------------------------------------------------------------------------
// Clone a template — Dexie first, then sync push.
// ---------------------------------------------------------------------------
export async function cloneImportTemplate(id: string, name?: string): Promise<ImportTemplate> {
  const db = await getSystemDb();
  const existing = await db.import_templates.get(id) as ImportTemplate | undefined;
  if (!existing || (existing as any)._deleted) {
    throw new Error('Import template not found in local database. Please allow sync to finish and try again.');
  }
  const now = new Date().toISOString();
  const data: ImportTemplate = {
    ...existing,
    _id: generateObjectId(),
    name: name ?? `${existing.name} (Copy)`,
    status: 'draft',
    isDraft: true,
    origin: 'system-web',
    allowedTenantIds: normalizeAllowedTenantIds(existing),
    createdAt: now,
    createdBy: 'system-web',
    updatedAt: now,
    updatedBy: 'system-web',
    publishedAt: undefined,
    publishedBy: undefined,
    archivedAt: undefined,
    deletedAt: undefined,
    deletedBy: undefined,
    _deleted: undefined,
  };
  await db.import_templates.put(data as any);
  wakeLocalWrite('import_templates');
  writeAuditLog({
    collection: 'importTemplates',
    documentId: data._id,
    action: 'clone',
    summary: `Cloned import template as "${data.name}"`,
  }).catch((err) => {
    console.warn('[systemImportTemplateService] Failed to write audit log for template clone:', err);
  });
  return data;
}

// ---------------------------------------------------------------------------
// Set visibility — delegates to Dexie-first updateImportTemplate.
// ---------------------------------------------------------------------------
export async function setTemplateVisibility(
  id: string,
  visibility: string,
  allowedTenantIds?: string[],
): Promise<ImportTemplate> {
  return updateImportTemplate(id, {
    visibility: visibility as ImportTemplate['visibility'],
    allowedTenantIds: allowedTenantIds ?? [],
  });
}

// ---------------------------------------------------------------------------
// Draft autosave — Dexie-only, no API call.
// Persists partial editor state locally; sync push will reconcile to MongoDB.
// Use this for autosave intervals in the template editor.
// ---------------------------------------------------------------------------
export async function saveDraftImportTemplate(
  id: string,
  patch: Partial<ImportTemplate>,
): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.import_templates.get(id);
  if (!existing) return;
  await db.import_templates.put({
    ...(existing as any),
    ...patch,
    isDraft: true,
    status: 'draft',
    updatedAt: new Date().toISOString(),
  });
  wakeLocalWrite('import_templates');
}
